/**
 * What the valley remembers (M5-01).
 *
 * The Elder's rumours are the ticket's hardest line — "the village never lies
 * about the world" — and the only way to keep that promise is to stop writing
 * rumours at all. What is written here instead is a **projection of the event
 * stream**: every event the world emits is offered to `remember`, a handful of
 * them are worth the village talking about, and those are kept as a short,
 * bounded list of tuples. The Elder's line is then rendered from a tuple the
 * same way a blow's line is rendered from a `BlowReport` — template over event,
 * with no path by which a sentence can exist without something having happened
 * to cause it.
 *
 * This is deliberately the rumour system's v0 (design spec §8's world cohesion),
 * and the shape is chosen so v1 is a bigger table rather than a rewrite: who
 * knows what, how a rumour distorts as it travels, and villagers disagreeing are
 * all additions to a `Happening`, not replacements for one.
 *
 * **Why it lives in world state.** Events are a fact about a *call* — `advance`
 * hands them back and keeps none — so a villager asked for news three hours
 * later has nothing to read. A memory that survives the tick has to be in the
 * world, and it is bounded and deterministic, which is what world state
 * requires: `CHRONICLE_LIMIT` entries, written from events that are themselves
 * a pure function of (state, intents).
 *
 * **Names, not ids.** A happening stores the name a thing had *when it
 * happened*, because that is what the news is: the wolf that took the cow is
 * gone by morning, its actor id resolves to nothing, and "something took
 * something" is not a rumour. The ids are kept alongside for the day M5-05's
 * provenance lines want to join back to the archive.
 */
import { TICKS_PER_GAME_DAY } from './time.js';
/* -------------------------------------------------------------------------- *
 * The record
 * -------------------------------------------------------------------------- */
/**
 * The kinds of thing the valley talks about.
 *
 * Coarser than the event vocabulary on purpose. The archive's job is to answer
 * "how did Gwion die" in forensic detail; a rumour's job is to be *the sort of
 * thing a person would mention*, and forty kinds of it would mean forty
 * template pools nobody writes well. Six is a village's worth of news.
 */
export const HAPPENING_KINDS = [
    /** Something with teeth killed a beast of the village's, in the village. */
    'raid',
    /** Somebody who lives here, or who plays here, is dead. */
    'death',
    /** A player took a serious wound and lived. The valley notices scars. */
    'hurt',
    /** A player killed something worth killing. */
    'kill',
    /** Sign of something big in the wood (M5-04 arms this). */
    'sign',
    /** A grave was opened. The valley's only sanction is memory; this is it. */
    'robbed',
];
/**
 * How many happenings the valley keeps.
 *
 * Small, and it is a *feature*: news gets stale, and a chronicle that kept a
 * thousand entries would have the Elder recounting a wolf raid from three weeks
 * ago as though it were this morning. Twenty is comfortably more than a game
 * day's worth of anything interesting, and it is bounded — which is the
 * condition for anything living in world state that a tick may append to.
 */
export const CHRONICLE_LIMIT = 20;
/**
 * How old news may be before nobody mentions it, in game days.
 *
 * Read at rumour time rather than enforced at write time, so the chronicle stays
 * a plain ring: a happening that has gone quiet is still in the record for
 * anything that wants to count it, it is simply not *news* any more.
 */
export const RUMOUR_DAYS = 3;
/**
 * Offer one event to the valley's memory.
 *
 * Called from `advance`'s emitter — the single point every event in the game
 * passes through — rather than from each rule that emits one, because a rule
 * that had to remember to file its own news is a rule that will eventually
 * forget. The filter below is the whole of the editorial judgement, and it is
 * deliberately narrow: most of what the world says happened is telemetry.
 */
export function remember(world, bystanders, event) {
    const happening = readEvent(world, bystanders, event);
    if (!happening)
        return;
    // Rebuilt rather than pushed into, because world state's arrays are declared
    // readonly and the draft is the only thing allowed to swap one out. The list
    // is twenty entries long; the copy is not the expensive part of a tick.
    const kept = [...world.chronicle, happening];
    world.chronicle =
        kept.length > CHRONICLE_LIMIT ? kept.slice(kept.length - CHRONICLE_LIMIT) : kept;
}
/** The editorial judgement: is this news, and whose? */
function readEvent(world, bystanders, event) {
    switch (event.type) {
        case 'died':
            return readDeath(world, bystanders, event);
        case 'blow':
            return readBlow(world, event);
        case 'status':
            return readSign(event);
        default:
            return null;
    }
}
/**
 * Sign of something big in the wood (M5-04).
 *
 * The one `status` event the valley talks about, distinguished by `refs[0]`
 * exactly as grave-robbing is distinguished inside `died`: the escalation in
 * `boar.ts` writes it when the chief stirs, and the Elder reads it back with the
 * `sign` pool. It carries no `who` or `what` — the news is that there is sign,
 * not who left it or who found it — and the templates that lean on neither are
 * the ones written for it.
 */
function readSign(event) {
    if (event.refs[0] !== 'sign')
        return null;
    return {
        tick: event.tick,
        kind: 'sign',
        who: '',
        what: '',
        actors: event.actors,
    };
}
/**
 * A death, read three ways.
 *
 * The animal a wolf took out of the pen is a **raid** and it is the news the
 * whole of M5-02 turns on; a person is a **death** and there is no more
 * important thing the village can be told; an animal a player killed is a
 * **kill**, which is how the Elder ends up congratulating somebody on a stag
 * without anybody having written a quest.
 *
 * `refs[0]` distinguishes the two shapes the sim emits: `carcass` for an animal
 * (`beasts.ts`), `corpse` or a cause for a person (`death.ts`, `combat.ts`).
 * Grave-robbing borrows the type and is caught first.
 */
function readDeath(world, bystanders, event) {
    const [subjectId, objectId] = event.actors;
    if (event.refs[0] === 'dig' && event.refs[1] === 'robbed') {
        const digger = subjectId ? world.actors[subjectId] : undefined;
        if (!digger)
            return null;
        return {
            tick: event.tick,
            kind: 'robbed',
            who: digger.name,
            what: nameOf(world, objectId) ?? 'a grave',
            actors: event.actors,
        };
    }
    // Burial is a `died` too. It is not news — the death already was.
    if (event.refs[0] === 'bury')
        return null;
    const dead = subjectId ? world.actors[subjectId] : undefined;
    if (!dead)
        return null;
    if (dead.kind === 'creature' && bystanders.livestock(dead.id)) {
        return {
            tick: event.tick,
            kind: 'raid',
            who: dead.name,
            what: nameOf(world, objectId) ?? '',
            actors: event.actors,
        };
    }
    if (dead.kind === 'creature') {
        // Only a person's kill is worth mentioning. A wolf that ate a rabbit is
        // the valley working, not the valley's news.
        const killer = objectId ? world.actors[objectId] : undefined;
        if (!killer || killer.kind !== 'player')
            return null;
        return {
            tick: event.tick,
            kind: 'kill',
            who: killer.name,
            what: dead.name,
            actors: [killer.id, dead.id],
        };
    }
    return {
        tick: event.tick,
        kind: 'death',
        who: dead.name,
        what: nameOf(world, objectId) ?? '',
        actors: event.actors,
    };
}
/**
 * How bad a wound has to be before the village hears about it.
 *
 * Level 3 on the M1-03 ladder is the rung where a blow stops being a scratch
 * and starts being something a person is still favouring a week later — which
 * is exactly the threshold the log's own prominence filter uses, and the right
 * one here for the same reason.
 */
export const NOTABLE_WOUND = 3;
/** A blow worth talking about: a serious one, on somebody who plays here. */
function readBlow(world, event) {
    const level = Number(event.refs[3] ?? 0);
    if (!Number.isFinite(level) || level < NOTABLE_WOUND)
        return null;
    const [attackerId, defenderId] = event.actors;
    const hurt = defenderId ? world.actors[defenderId] : undefined;
    if (!hurt)
        return null;
    // Players and the people who live here. What a wolf took off a stag is not
    // something anybody in the village saw.
    if (hurt.kind === 'creature')
        return null;
    return {
        tick: event.tick,
        kind: 'hurt',
        who: hurt.name,
        what: nameOf(world, attackerId) ?? '',
        actors: event.actors,
    };
}
function nameOf(world, id) {
    if (id === undefined)
        return null;
    return world.actors[id]?.name ?? null;
}
/* -------------------------------------------------------------------------- *
 * Reading it back
 * -------------------------------------------------------------------------- */
/**
 * The freshest piece of news, or `null` if the valley has been quiet.
 *
 * Newest first, and one at a time: a villager asked for news gives you *the*
 * news, and the player who asks again an hour later gets the same answer until
 * something else happens. That is what a small village is like, and it is also
 * what makes the answer worth listening to — an Elder who recited the last
 * twenty events would be a log pane with a beard.
 *
 * `kinds` is how the caller varies who knows what: the herders are asked about
 * the pen and answer about the pen. Omitted, any news will do.
 */
export function latestNews(chronicle, tick, kinds) {
    const oldest = tick - RUMOUR_DAYS * TICKS_PER_GAME_DAY;
    for (let index = chronicle.length - 1; index >= 0; index -= 1) {
        const happening = chronicle[index];
        if (!happening)
            continue;
        if (happening.tick < oldest)
            return null;
        if (kinds && !kinds.includes(happening.kind))
            continue;
        return happening;
    }
    return null;
}
/** How long ago, in game days. 0 is today, 1 is yesterday. */
export function daysAgo(happening, tick) {
    return Math.max(0, Math.floor((tick - happening.tick) / TICKS_PER_GAME_DAY));
}
/**
 * Fill a template out of one happening, or `null` if this world has no words
 * for that kind.
 *
 * `tick` picks the template, so a replay hears the same phrasing in the same
 * order — the same rule the villagers' own lines are chosen by.
 */
export function renderRumour(pools, happening, tick) {
    const templates = pools.lines[happening.kind];
    const template = templates?.[tick % templates.length];
    if (!template)
        return null;
    const days = daysAgo(happening, tick);
    const when = pools.when[Math.min(days, pools.when.length - 1)] ?? '';
    return template
        .replaceAll('{who}', happening.who)
        .replaceAll('{what}', happening.what)
        .replaceAll('{when}', when);
}
//# sourceMappingURL=chronicle.js.map